<template>
  <div class="mx-auto max-w-2xl px-4 py-8">
    <div class="mb-8 text-center">
      <h1 class="text-3xl font-bold tracking-tight text-primary">Contact Support</h1>
      <p class="mt-2 text-lg text-gray-600">
        We're here to help. Fill out the form below and we'll get back to you as soon as
        possible.
      </p>
    </div>
    <div class="rounded-lg border bg-white p-8 shadow-sm contact-form">
      <form @submit.prevent="onSubmit" class="space-y-8">
        <div>
          <label for="category" class="block text-sm font-medium text-gray-700">Support Category</label>
          <select v-model="form.category" id="category"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm">
            <option value="" disabled>Select a category</option>
            <option v-for="category in supportCategories" :key="category.value" :value="category.value">
              {{ category.label }}
            </option>
          </select>
        </div>
        <div>
          <label for="subject" class="block text-sm font-medium text-gray-700">Subject</label>
          <input v-model="form.subject" type="text" id="subject"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="Enter the subject of your inquiry" />
          <span v-if="errors.subject" class="text-red-500 text-sm">{{
            errors.subject
            }}</span>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
            <input v-model="form.name" type="text" id="name"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="Your name" />
            <span v-if="errors.name" class="text-red-500 text-sm">{{ errors.name }}</span>
          </div>
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
            <input v-model="form.email" type="email" id="email"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="Your email address" />
            <span v-if="errors.email" class="text-red-500 text-sm">{{
              errors.email
              }}</span>
          </div>
        </div>
        <div>
          <label for="message" class="block text-sm font-medium text-gray-700">Message</label>
          <textarea v-model="form.message" id="message" rows="4"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="Please describe your issue or question in detail"></textarea>
          <span v-if="errors.message" class="text-red-500 text-sm">{{
            errors.message
            }}</span>
        </div>
        <button type="submit"
          class="w-full flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          :disabled="isLoading">
          <span v-if="isLoading" class="flex items-center">
            <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            Sending...
          </span>
          <span v-else>Send Support Request</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, getCurrentInstance } from "vue";
import { useAuthStore } from "@/stores/auth";
import { Api } from "@/lib";

const authStore = useAuthStore();
const app = getCurrentInstance();
const api = app?.appContext.config.globalProperties.$api as Api;

const supportCategories = [
  { value: "Contact Us", label: "Contact Us" },
  { value: "Change Plan", label: "Change Plan" },
  { value: "Move Address", label: "Move Address" },
];

const form = reactive({
  category: "",
  subject: "",
  name: "",
  email: "",
  message: "",
  responseMethod: "email",
  phone: "",
});

const errors = reactive({
  category: "",
  subject: "",
  name: "",
  email: "",
  message: "",
});

const isLoading = ref(false);

const validateEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

const validateForm = () => {
  let valid = true;
  if (!form.name) {
    errors.name = "Name is required.";
    valid = false;
  } else {
    errors.name = "";
  }
  if (!form.email) {
    errors.email = "Email is required.";
    valid = false;
  } else if (!validateEmail(form.email)) {
    errors.email = "Invalid email format.";
    valid = false;
  } else {
    errors.email = "";
  }
  if (!form.subject) {
    errors.subject = "Subject is required.";
    valid = false;
  } else {
    errors.subject = "";
  }
  if (!form.message) {
    errors.message = "Message is required.";
    valid = false;
  } else {
    errors.message = "";
  }
  return valid;
};

const onSubmit = async () => {
  if (!validateForm()) {
    return;
  }
  isLoading.value = true;
  try {
    const accessToken = await authStore.getAccessTokenSilently();

    const response = await api.post(
      "zendesk/createticket",
      {
        category: form.category,
        subject: form.subject,
        name: form.name,
        email: form.email,
        message: form.message,
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (response.status === 200) {
      alert("Support request submitted successfully. A Zendesk ticket has been created.");
      // Reset form
      Object.keys(form).forEach((key) => {
        (form as any)[key as keyof typeof form] = "";
      });

      if (authStore.user) {
        form.name = authStore.user.name || "";
        form.email = authStore.user.email || "";
      }
    } else {
      throw new Error("Failed to submit support request.");
    }
  } catch (err) {
    console.error(err);
    alert("Failed to submit support request.");
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await authStore.loadUser();

  if (authStore.user) {
    form.name = authStore.user.name || "";
    form.email = authStore.user.email || "";
  }
});
</script>

<style scoped>
.contact-form {
  background-color: #d7e6f2;
  color: black;
}
</style>
